import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismLoadingBar } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import SettingsRecommendationsMinimumPriceDifference from 'lumen/components/settings/recommendations/minimum-price-difference';
import SettingsRecommendationsRecommendationType from 'lumen/components/settings/recommendations/recommendation-type';
import SettingsRecommendationsRounding from 'lumen/components/settings/recommendations/rounding';
import SettingsRecommendationsSnowflakesRecommendationsSettingCard from 'lumen/components/settings/recommendations/snowflakes/recommendations-setting-card';
import type HotelSettingsRecommendationsController from 'lumen/controllers/hotel/settings/recommendations';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';

interface HotelSettingsRecommendationsSignature {
  Args: {
    controller: HotelSettingsRecommendationsController;
    model: unknown;
  };
}

export default class Recommendations extends Component<HotelSettingsRecommendationsSignature> {
  @service declare access: Services['access'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceRangeSettingsTranslations() {
    if (this.args.controller.access.hasPricingAssistantOpenPricingFeature) {
      return {
        title: this.intl.t(
          'pricing_assistant.recommendations.settings.pricing_range.multi_room.title',
        ),
        description: this.intl.t(
          'pricing_assistant.recommendations.price_range.multi-room.explanation',
        ),
      };
    }
    return {
      title: this.intl.t(
        'pricing_assistant.recommendations.settings.pricing_range.title',
      ),
      description: this.intl.t(
        'pricing_assistant.recommendations.settings.pricing_range.description',
      ),
    };
  }

  get showRollingReferenceRatesCard(): boolean {
    const isORPorDRPSubsription =
      this.access.hasPricingAssistantOpenPricingFeature ||
      this.access.hasPricingAssistantDynamicRatePricingFeature;

    return (
      isORPorDRPSubsription &&
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.ROLLING_WINDOW_REFERENCE_RATES,
      )
    );
  }

  get showRoomHierarchyCard(): boolean {
    const isHotelWithMultipleRoomTypes =
      this.access.hasPricingAssistantOpenPricingFeature ||
      this.access.hasPricingAssistantDynamicRatePricingFeature;
    return (
      this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.PRICING_MANAGER_ROOM_HIERARCHY_ROOM_OFFSETS,
      ) && isHotelWithMultipleRoomTypes
    );
  }

  <template>
    {{#if
      @controller.model.pricingAssistantRecommendationsStrategiesTask.isRunning
    }}
      <PrismLoadingBar />
    {{else}}
      <div class="flex flex-1 flex-col">
        {{#if @controller.isIndexRoute}}
          <SettingsPageTemplate
            @description={{t
              "pricing_assistant.recommendations.settings.subtitle"
            }}
            @hasAccountSelector={{true}}
            @title={{t "pricing_assistant.recommendations.settings.main_title"}}
            @type="container"
          >
            <:link>
              <a
                class="prism-link"
                href="https://help.mylighthouse.com/en/articles/7857322-how-do-i-customize-my-recommendations"
                rel="noopener noreferrer"
                target="_blank"
              >{{t "general.learn_more"}}</a>
            </:link>
            <:default>
              <div class="flex flex-col gap-300">
                <SettingsRecommendationsSnowflakesRecommendationsSettingCard
                  @description={{t
                    "pricing_assistant.recommendations.settings.competitor_impact.description"
                  }}
                  @illustration="40x40_balance"
                  @routeName="hotel.settings.recommendations.competitors-impact"
                  @title={{t
                    "pricing_assistant.recommendations.settings.competitor_impact.title"
                  }}
                />
                {{#if this.showRoomHierarchyCard}}
                  <SettingsRecommendationsSnowflakesRecommendationsSettingCard
                    @description={{t
                      "pricing_assistant.recommendations.settings.room_hierarchy.description"
                    }}
                    @illustration="40x40-room-hierarchy"
                    @routeName="hotel.settings.recommendations.room-hierarchy"
                    @title={{t
                      "pricing_assistant.recommendations.settings.room_hierarchy.title"
                    }}
                  />
                {{/if}}
                <SettingsRecommendationsSnowflakesRecommendationsSettingCard
                  @description={{this.priceRangeSettingsTranslations.description}}
                  @illustration="40x40_range-vertical"
                  @routeName="hotel.settings.recommendations.price-range"
                  @title={{this.priceRangeSettingsTranslations.title}}
                />
                {{#if this.showRollingReferenceRatesCard}}
                  <SettingsRecommendationsSnowflakesRecommendationsSettingCard
                    @description={{t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.description"
                    }}
                    @illustration="40x40_range-vertical"
                    @routeName="hotel.settings.recommendations.rolling-reference-rates"
                    @title={{t
                      "pricing_assistant.recommendations.settings.rolling_reference_rates.title"
                    }}
                  />
                {{/if}}
                <SettingsRecommendationsMinimumPriceDifference
                  @model={{@controller.model}}
                  @strategy={{@controller.strategiesPerType.minimumDifferenceStrategy}}
                />
                <SettingsRecommendationsRecommendationType
                  @model={{@controller.model}}
                  @strategy={{@controller.strategiesPerType.allowedRecommendationTypeStrategy}}
                />
                <SettingsRecommendationsRounding
                  @model={{@controller.model}}
                  @strategy={{@controller.strategiesPerType.roundingStrategy}}
                />
              </div>
            </:default>
          </SettingsPageTemplate>
        {{/if}}
        {{outlet}}
      </div>
    {{/if}}
  </template>
}
